import type { ReactNode } from "react";
import { deriveTier, type CompetencyTier } from "@talenthop/shared";

const TIER_LABEL: Record<CompetencyTier, string> = { low: "Low", medium: "Medium", high: "High" };
const TIERS: CompetencyTier[] = ["low", "medium", "high"];

/**
 * A Low/Medium/High quick-set control layered on top of the existing 1-100 relative competency
 * weight — never a second source of truth. The highlighted segment is derived live from `weight`
 * against `siblingWeights` (see `deriveTier`, packages/shared/src/competencyImportance.ts);
 * clicking a segment hands the caller that tier to convert back into numbers via `applyTier`.
 * Shared between the admin vertical editor and the per-requisition weight override — same
 * control, two different callers. The caller should not render this at all when a set has fewer
 * than two competencies — tiering is meaningless when one competency IS the whole composite.
 */
export function TierToggle({
  weight,
  siblingWeights,
  onChange,
}: {
  weight: number;
  siblingWeights: number[];
  onChange: (tier: CompetencyTier) => void;
}): ReactNode {
  const active = deriveTier(weight, siblingWeights);
  return (
    <div className="seg" role="group" aria-label="Importance">
      {TIERS.map((tier) => (
        <button
          key={tier}
          type="button"
          className={active === tier ? "on" : ""}
          onClick={() => onChange(tier)}
        >
          {TIER_LABEL[tier]}
        </button>
      ))}
    </div>
  );
}
